import { useState } from "react";
import { Wrench, Loader2, MessageSquare, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";

/**
 * Iter 349.f (2026-02-25) — Technician-added ad-hoc task/comment.
 * Iter 349.h (2026-02-25) — REBUILT to align with regular tasks per
 * Dirk: "don't build something completely new, just align it with
 * the way the other task is. It's just gated behind a different
 * button for the Add Technician task or comment."
 *
 * The old version popped a custom blue modal with its own photo/video
 * pickers — this diverged from the app design and, on Android, the
 * plain <input type=file capture> only opened the camera roll and
 * never offered a "Take Photo" / "Choose from Gallery" chooser
 * (which the existing PhotoPickerButton on every task row DOES
 * handle correctly).
 *
 * New flow:
 *   1. Click button → tiny shadcn dropdown ("Add comment" / "Log task
 *      carried out").
 *   2. Pick one → POST /tech-notes with empty description.
 *   3. Parent reloads the jobcard, the new tech_note task appears in
 *      the checklist like any other row.
 *   4. `onCreated(job, newId)` is called so the parent can auto-focus
 *      the new row's label editor (identical to `addItem` pattern).
 *   5. Photos, edit, status, delete — all use the existing task-row
 *      controls. No custom pickers. Same UX on iPhone / Android /
 *      desktop as any other task.
 *
 * Non-permitted roles see a friendly toast instead of the dropdown.
 */
export default function AddTechNoteButton({
  jobId,
  variant = "top",
  onCreated,
  className = "",
}) {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const isAllowed = role === "technician" || role === "owner";
  const [busy, setBusy] = useState(false);

  const create = async (noteType /* "comment" | "task_done" */) => {
    if (!isAllowed) {
      toast.error(
        "Only technicians and owners can add ad-hoc tasks or comments on site.",
      );
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/jobcards/${jobId}/tech-notes`, {
        description: "",
        note_type: noteType,
      });
      const newId = r?.data?.item?.id || null;
      toast.success(
        noteType === "task_done"
          ? "Task added — tap to describe what you did."
          : "Comment added — tap to describe what you observed.",
      );
      onCreated?.(r?.data?.jobcard || null, newId);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not add. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const labelText =
    variant === "top"
      ? "Add tech task or comment"
      : "Add another tech task or comment";

  // Non-permitted roles: render the button as a hint-only element that
  // toasts on click so they see the affordance exists but can't use it.
  if (!isAllowed) {
    return (
      <button
        type="button"
        onClick={() => create("comment")}
        data-testid={`jc-tech-note-btn-${variant}`}
        title="Only technicians and owners can add these."
        className={`w-full flex items-center justify-center gap-2 rounded-md border border-dashed border-white/10 bg-white/[0.02] px-3 py-2 text-xs font-medium text-[#647087] hover:text-[#a0aabf] transition ${className}`}
      >
        <Wrench className="w-3.5 h-3.5" />
        {labelText}
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={busy}
          data-testid={`jc-tech-note-btn-${variant}`}
          title="Log an ad-hoc task you carried out or leave an on-site comment."
          className={`w-full flex items-center justify-center gap-2 rounded-md border border-dashed border-[#1FB6C1]/40 bg-[#1FB6C1]/[0.05] px-3 py-2 text-xs font-medium text-[#a0e6ea] hover:bg-[#1FB6C1]/[0.10] hover:border-[#1FB6C1]/70 disabled:opacity-50 transition ${className}`}
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Wrench className="w-3.5 h-3.5" />
          )}
          {labelText}
          <ChevronDown className="w-3 h-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      {/* Iter 349.i (2026-02-25) — Force app-native panel colours to
          override shadcn defaults (which resolve to a bluish popover
          on this theme). Matches the standard dark panel used by
          GpsRequiredModal, JC modals etc. */}
      <DropdownMenuContent
        align="center"
        sideOffset={6}
        className="min-w-[16rem] rounded-md border border-white/10 bg-[#121419] p-1 text-[#e0e7f0] shadow-2xl"
        data-testid="jc-tech-note-menu"
      >
        <DropdownMenuItem
          onSelect={() => create("comment")}
          data-testid="jc-tech-note-menu-comment"
          className="cursor-pointer gap-2 rounded px-2.5 py-2 text-[#e0e7f0] focus:bg-white/[0.06] data-[highlighted]:bg-white/[0.06]"
        >
          <MessageSquare className="w-4 h-4 shrink-0 text-[#a0aabf]" />
          <div className="flex flex-col min-w-0">
            <span className="text-sm">Add comment</span>
            <span className="text-[10px] text-[#647087]">
              An on-site observation
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => create("task_done")}
          data-testid="jc-tech-note-menu-task"
          className="cursor-pointer gap-2 rounded px-2.5 py-2 text-[#e0e7f0] focus:bg-white/[0.06] data-[highlighted]:bg-white/[0.06]"
        >
          <Wrench className="w-4 h-4 shrink-0 text-[#a0aabf]" />
          <div className="flex flex-col min-w-0">
            <span className="text-sm">Log task carried out</span>
            <span className="text-[10px] text-[#647087]">
              Something you did that wasn't planned
            </span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
